import Card from '@/components/common/Card'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import React, { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { callApi, ThrowError } from '@/api/callApi'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import type { EmployeeOnBoard, IEmployee } from '@/interface/employeeOnBoard'
import OnboardBasicDetail from './EmployeeOnboard/OnboardBasicDetail'
import OnboardPermissions from './EmployeeOnboard/OnboardPermissions'
import OnboardAdvanceDetails from './EmployeeOnboard/OnboardAdvanceDetails'
import OnboardAddress from './EmployeeOnboard/OnboardAddress'
import OnboardBankDetails from './EmployeeOnboard/OnboardBankDetails'
import OnboardNominee from './EmployeeOnboard/OnboardNominee'

import { API_ENDPOINT } from '@/api/constant'
import OnboardClient from './EmployeeOnboard/OnboardClient'

const CreateEmployee: React.FC = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const [isProfile] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState("tab1")
    const [createdId, setCreatedId] = useState<string | null>(null)
    const currentId = id || createdId

    const formatDateForInput = (value?: string | null) => value ? value.split('T')[0] : ''

    const normalizeEmployeeForForm = (employee: IEmployee): EmployeeOnBoard => {
        const userDetail = employee.userDetail

        return {
            name: employee.name || '',
            email: employee.email || '',
            phone: employee.phone || '',
            employeeCode: employee.employeeCode || '',
            type: String(employee.type ?? '') as EmployeeOnBoard['type'],
            gender: String(employee.gender ?? '') as EmployeeOnBoard['gender'],
            status: (employee.status || '') as EmployeeOnBoard['status'],
            dob: formatDateForInput(employee.dob),
            joiningDate: formatDateForInput(employee.joiningDate),
            personalEmail: employee.personalEmail || '',
            bloodGroup: employee.bloodGroup || '',
            profilePhoto: employee.profilePhoto || '',
            isMarried: String(Boolean(employee.isMarried)) as never,
            skillType: employee.skillType || '',
            permissions: Array.isArray(employee.permissions) ? employee.permissions.join(',') : employee.permissions || '',
            isAssignedAllSite: employee.isAssignedAllSite ?? false,
            assignedSiteType: employee.assignedSiteType || '',
            departmentId: employee.departmentId || '',
            designationId: employee.designationId || '',
            shiftId: employee.shiftId || '',
            siteId: employee.siteId || '',
            branchId: employee.branchId || '',
            clientId: '',
            fatherName: userDetail?.fatherName || '',
            motherName: userDetail?.motherName || '',
            uanNumber: userDetail?.uanNumber || '',
            aadharNumber: userDetail?.aadharNumber || '',
            esicNumber: userDetail?.esicNumber || '',
            insuranceNumber: userDetail?.insuranceNumber || '',
            pfNumber: userDetail?.pfNumber || '',
            panNumber: userDetail?.panNumber || '',
            drivingLicenseNumber: userDetail?.drivingLicenseNumber || '',
            address: userDetail?.address || '',
            state: userDetail?.state || '',
            city: userDetail?.city || '',
            pinCode: userDetail?.pinCode || '',
            permanentAddress: userDetail?.permanentAddress || '',
            isSameAsPermanentAddress: userDetail?.isSameAsPermanentAddress ?? false,
            permanentState: userDetail?.permanentState || '',
            permanentCity: userDetail?.permanentCity || '',
            permanentPinCode: userDetail?.permanentPinCode || '',
            bankName: userDetail?.bankName || '',
            ifscCode: userDetail?.ifscCode || '',
            accountHolderName: userDetail?.accountHolderName || '',
            accountNumber: userDetail?.accountNumber || '',
            branchAddress: userDetail?.branchAddress || '',
            nomineeName: userDetail?.nomineeName || '',
            nomineePhone: userDetail?.nomineePhone || '',
            nomineeDob: formatDateForInput(userDetail?.nomineeDob),
            nomineeRelation: userDetail?.nomineeRelation || '',
            ecpName: userDetail?.ecpName || '',
            ecpPhone: userDetail?.ecpPhone || '',
            previousCompanyName: userDetail?.previousCompanyName || '',
            previousDesignationName: userDetail?.previousDesignationName || '',
            previousFromDate: formatDateForInput(userDetail?.previousFromDate),
            previousToDate: formatDateForInput(userDetail?.previousToDate),
            currentCompany: userDetail?.currentCompany ?? false,
            ctc: userDetail?.ctc ?? '',
            basicSalary: userDetail?.basicSalary ?? '',
            hra: userDetail?.hra ?? '',
            otherAllowances: userDetail?.otherAllowances ?? '',
            user: { id: employee.id },
            userDetail: userDetail || undefined,
            oauthClients: employee.oauthClients || [],
            password: employee?.password,
            // campus: employee.campus?.name || null,
            campusId: employee.campusId || '',
            clientIds: (employee.oauthClients || []).map(oc => oc.id).filter(Boolean) as string[],
        }
    }

    // FormProvider — shared across all tabs
    const methods = useForm<EmployeeOnBoard>()


    // Fetch employee data for edit mode
    useEffect(() => {
        if (!id) return
        const fetchEmployee = async () => {
            setIsLoading(true)
            try {
                const response = await EmployeeOnBoardService.getEmployeeById(id)
                if (response?.status) {
                    methods.reset(normalizeEmployeeForForm(response.data))
                }
            } catch (error) {
                ThrowError(error)
            } finally {
                setIsLoading(false)
            }
        }
        fetchEmployee()
    }, [id, methods])

    // File upload helper
    const uploadFile = async (file: File): Promise<string | null> => {
        const formData = new FormData()
        formData.append('file', file)
        try {
            const response = await callApi(`${API_ENDPOINT.fileUpload}/upload`, {
                method: "POST",
                data: formData,
                multipart: true,
            })
            if (response?.status) {
                return response.data.path
            }
            return null
        } catch (error) {
            ThrowError(error)
            return null
        }
    }

    // Generic save function for each step - now sends JSON
    const onStepSubmit = async (data: Partial<EmployeeOnBoard>, nextTab?: string) => {
        setIsLoading(true)
        try {
            if (currentId) {
                // Update mode
                const response = await EmployeeOnBoardService.editEmployee(data, currentId)
                if (response?.status) {
                    showToastNotification('success', response.message)
                    if (nextTab) {
                        setActiveTab(nextTab)
                    } else {
                        navigate(RoutesLink.superAdminEmployees)
                    }
                }
            } else {
                // Create mode
                const response = await EmployeeOnBoardService.createEmployee(data as EmployeeOnBoard)
                if (response?.status) {
                    showToastNotification('success', response.message)
                    const newEmpId = (response?.data?.user?.id)
                    if (newEmpId) {
                        setCreatedId(newEmpId)
                        navigate(`${RoutesLink.editEmployee}/${newEmpId}`)
                        if (nextTab) {
                            setActiveTab(nextTab)
                        }
                    }
                }
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleNextStep = async (nextTab: string, fieldsToValidate: (keyof EmployeeOnBoard)[]) => {
        const isValid = await methods.trigger(fieldsToValidate)
        if (isValid) {
            // Get values as an object for the validated fields
            const stepData: Partial<EmployeeOnBoard> = {}
            fieldsToValidate.forEach((field) => {
                (stepData as Record<string, unknown>)[field] = methods.getValues(field)
            })

            if (currentId) {
                // If ID exists, we are in Edit mode. 
                // As per request, only send fields from the current tab to the edit API.
                onStepSubmit(stepData, nextTab)
            } else if (activeTab === "tab2") {
                // Creating the employee record for the first time
                // Send all data (Basic Details + Permissions)
                onStepSubmit(methods.getValues(), nextTab)
            } else {
                // Just move to the next tab (usually from Tab 1 to Tab 2)
                setActiveTab(nextTab)
            }
        }
    }

    const handleClientSubmit = async () => {
        const isValid = await methods.trigger("clientIds")
        if (!isValid) return
        const clientIds = methods.getValues("clientIds")
        try {
            setIsLoading(true)
            const response = await callApi(`${API_ENDPOINT.user}/${currentId}/clients`, {
                method: "POST",
                data: {
                    clientIds: clientIds,
                }
            })
            if (response?.status) {
                showToastNotification('success', response.message)
                navigate(RoutesLink.superAdminEmployees)
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const tabProps = { isLoading, isProfile, empId: currentId || null, handleNextStep, uploadFile, handleClientSubmit }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading title={id ? "Edit Employee" : "Create Employee"} isBackButtonVisible />
                <Card>
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit((data) => onStepSubmit(data))}>
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

                                <TabsList className="w-max lg:w-full bg-gray-50/50 p-1.5 flex flex-nowrap overflow-x-auto overflow-y-hidden justify-start  h-fit gap-1.5 rounded-2xl border border-gray-100/50 shadow-inner mb-6 scroll-bar-hidden">
                                    <TabsTrigger value="tab1" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Basic Details</TabsTrigger>
                                    <TabsTrigger value="tab2" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Permissions</TabsTrigger>
                                    <TabsTrigger value="tab3" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Advance Details</TabsTrigger>
                                    <TabsTrigger value="tab4" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Address</TabsTrigger>
                                    <TabsTrigger value="tab5" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Bank Details</TabsTrigger>
                                    <TabsTrigger value="tab6" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Nominee</TabsTrigger>
                                    {/* <TabsTrigger value="tab7" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Previous Work</TabsTrigger> */}
                                    {/* <TabsTrigger value="tab7" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Documents</TabsTrigger>
                                    <TabsTrigger value="tab8" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Salary Detail</TabsTrigger> */}
                                    <TabsTrigger value="tab9" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-primary flex-1 min-w-[130px] py-2.5 font-medium transition-all">Application</TabsTrigger>
                                </TabsList>
                                <TabsContent value="tab1">
                                    <OnboardBasicDetail {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab2">
                                    <OnboardPermissions {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab3">
                                    <OnboardAdvanceDetails {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab4">
                                    <OnboardAddress {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab5">
                                    <OnboardBankDetails {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab6">
                                    <OnboardNominee {...tabProps} />
                                </TabsContent>
                                {/* <TabsContent value="tab7">
                                    <OnboardPreviousWork {...tabProps} />
                                </TabsContent> */}
                                {/* <TabsContent value="tab7">
                                    <OnboardDocuments {...tabProps} />
                                </TabsContent>
                                <TabsContent value="tab8">
                                    <OnboardSalaryDetail {...tabProps} />
                                </TabsContent> */}
                                <TabsContent value="tab9">
                                    <OnboardClient {...tabProps} />
                                </TabsContent>
                            </Tabs>
                        </form>
                    </FormProvider>
                </Card>

            </div>
        </Layout>
    )
}

export default CreateEmployee